import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { searchUsers } from "../../services/Zendesk";
import type {
  InputSearchProps,
  SearchItem,
  UserResult,
} from "./interfaces";

const normalizeUser = (user: unknown): UserResult => {
  const userObj = user as {
    id: number;
    name?: string;
    email?: string;
    role?: string;
    organization?: { name?: string };
  };
  
  return {
    kind: "user",
    id: String(userObj.id),
    name: userObj.name || userObj.email || "(sem nome)",
    email: userObj.email,
    role: userObj.role,
    organization: userObj.organization?.name,
  };
};

export const defaultUserSearchFn = async (
  term: string
): Promise<UserResult[]> => {
  console.log("🔧 DEBUG: defaultUserSearchFn chamado com termo:", term);
  
  const t = term?.trim();
  if (!t) {
    console.log("🔧 DEBUG: Termo vazio, retornando array vazio");
    return [];
  }

  console.log("🔧 DEBUG: Chamando searchUsers...");
  const users = await searchUsers(term);
  console.log("🔧 DEBUG: searchUsers retornou:", users);
  
  const normalizedUsers = users.map(normalizeUser);
  console.log("🔧 DEBUG: Usuários normalizados:", normalizedUsers);
  
  return normalizedUsers;
};

function useDebouncedCallback(
  fn: (term: string) => Promise<void>,
  delay: number
) {
  const timer = useRef<number | null>(null);
  const cb = useCallback(
    (term: string) => {
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => fn(term), delay);
    },
    [fn, delay]
  );
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );
  return cb;
}

export function useUserSearch({
  searchFn,
  minChars = 2,
  debounceMs = 300,
  initialValue,
  onSelect,
}: Omit<InputSearchProps, "mode" | "objectTypeKey">) {
  const [query, setQuery] = useState(initialValue || "");
  const [results, setResults] = useState<SearchItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);
  const [error, setError] = useState<string | null>(null);
  const [userHasTyped, setUserHasTyped] = useState(false);

  // Sincronizar com mudanças no initialValue apenas se o usuário não estiver digitando
  useEffect(() => {
    if (initialValue !== undefined && initialValue !== query && !userHasTyped) {
      setQuery(initialValue);
    }
  }, [initialValue, query, userHasTyped]);

  const effectiveSearch = useMemo(() => {
    if (searchFn) return searchFn;
    return defaultUserSearchFn;
  }, [searchFn]);

  const doSearch = useCallback(
    async (term: string) => {
      console.log("🔧 DEBUG: doSearch chamado com termo:", term);
      console.log("🔧 DEBUG: term.trim().length:", term.trim().length);
      console.log("🔧 DEBUG: minChars:", minChars);
      
      if (term.trim().length < minChars) {
        console.log("🔧 DEBUG: Termo muito curto, não fazendo busca");
        setResults([]);
        setOpen(false);
        setLoading(false);
        setError(null);
        return;
      }
      
      console.log("🔧 DEBUG: Iniciando busca...");
      setLoading(true);
      setError(null);
      try {
        console.log("🔧 DEBUG: Chamando effectiveSearch...");
        const list = await effectiveSearch(term.trim());
        console.log("🔧 DEBUG: Resultado da busca:", list);
        setResults(list);
        setOpen(true);
        setHighlight(list.length ? 0 : -1);
      } catch (err) {
        const error = err as {
          response?: { data?: { error?: string } };
          message?: string;
        };
        const msg =
          error?.response?.data?.error || error?.message || "Falha na busca";
        setError(msg);
        setResults([]);
        setOpen(true);
        setHighlight(-1);
      } finally {
        setLoading(false);
      }
    },
    [effectiveSearch, minChars]
  );

  const debouncedSearch = useDebouncedCallback(doSearch, debounceMs);

  const handleChange = (value: string) => {
    console.log("🔧 DEBUG: handleChange chamado com:", value);
    console.log("🔧 DEBUG: minChars:", minChars);
    console.log("🔧 DEBUG: debounceMs:", debounceMs);
    setUserHasTyped(true);
    setQuery(value);
    debouncedSearch(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && highlight < results.length) {
        const picked = results[highlight];
        setUserHasTyped(false); // Reset da flag quando seleciona via teclado
        onSelect(picked);
        setQuery(picked.name);
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleBlur = () => setTimeout(() => setOpen(false), 120);

  const handleSelect = (item: SearchItem) => {
    console.log("🔧 DEBUG: handleSelect chamado para usuário:", item);
    setUserHasTyped(false); // Reset da flag quando seleciona
    onSelect(item);
    setQuery(item.name);
    setOpen(false);
    console.log("✅ DEBUG: onSelect executado com sucesso para usuário");
  };

  const clearQuery = () => {
    console.log("🔧 DEBUG: Limpando query do usuário");
    setUserHasTyped(false); // Reset da flag quando limpa
    setQuery("");
    setResults([]);
    setOpen(false);
    setError(null);
  };

  return {
    query,
    results,
    open,
    loading,
    error,
    highlight,
    handleChange,
    handleKeyDown,
    handleBlur,
    handleSelect,
    clearQuery,
  };
}
